import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, MessageCircle } from 'lucide-react';
import {
  SJChatMessage,
  SJChatStreamingIndicator,
  SJChatInput,
  SJChatSuggestions,
  SJChatGreeting,
  SJChatCitations,
} from '@/components/ask';
import { useAskSanjoy } from '@/hooks/use-ask-sanjoy';
import { useConversationStore } from '@/store/conversation';

export function AskSanjoyDrawer() {
  const { drawerOpen, closeDrawer } = useConversationStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isStreaming,
    streamingContent,
    suggestedQuestions,
    citations,
    error,
    sendMessage,
    startNewConversation,
  } = useAskSanjoy();

  const hasMessages = messages.length > 0;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, streamingContent]);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape' && drawerOpen) closeDrawer();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [drawerOpen, closeDrawer]);

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-[2px]"
            onClick={closeDrawer}
            aria-hidden="true"
          />

          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-y-0 right-0 z-[201] flex w-full flex-col bg-[var(--sj-bg)] shadow-2xl pt-safe md:w-1/2"
            role="dialog"
            aria-modal="true"
            aria-label="Ask Sanjoy"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[var(--sj-border)] px-4 py-3">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-[#de4c49]" />
                <span className="text-sm font-semibold text-[var(--sj-text-primary)]">
                  Ask Sanjoy
                </span>
              </div>
              <div className="flex items-center gap-1">
                {hasMessages && (
                  <button
                    type="button"
                    onClick={startNewConversation}
                    className="touch-target inline-flex items-center gap-1.5 rounded-[8px] px-2.5 text-xs text-[var(--sj-text-secondary)] transition-colors hover:bg-[var(--sj-surface)]"
                  >
                    <RotateCcw className="h-3 w-3" />
                    <span className="hidden sm:inline">New</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="touch-target inline-flex items-center justify-center rounded-[8px] text-[var(--sj-text-muted)] transition-colors hover:bg-[var(--sj-surface)] hover:text-[var(--sj-text-primary)]"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-4 py-6">
              {!hasMessages && !isStreaming ? (
                <div className="flex h-full flex-col items-center justify-center">
                  <SJChatGreeting context={{}} />
                  <div className="mt-6 w-full max-w-md">
                    <p className="mb-3 text-center text-xs font-medium text-[var(--sj-text-muted)]">
                      Try asking about:
                    </p>
                    <SJChatSuggestions
                      questions={suggestedQuestions}
                      onSelect={sendMessage}
                      disabled={isStreaming}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <SJChatMessage key={msg.id} message={msg} />
                  ))}

                  {isStreaming && (
                    <SJChatStreamingIndicator content={streamingContent} />
                  )}

                  {!isStreaming && citations.length > 0 && (
                    <SJChatCitations citations={citations} />
                  )}

                  {!isStreaming && hasMessages && suggestedQuestions.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="pt-2"
                    >
                      <SJChatSuggestions
                        questions={suggestedQuestions}
                        onSelect={sendMessage}
                        disabled={isStreaming}
                      />
                    </motion.div>
                  )}

                  {error && (
                    <div className="rounded-[12px] border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30">
                      <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                      <button
                        onClick={() => {
                          const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
                          if (lastUserMsg) sendMessage(lastUserMsg.content);
                        }}
                        className="mt-2 text-xs font-medium text-red-600 hover:text-red-800 dark:text-red-400"
                      >
                        Try again
                      </button>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="border-t border-[var(--sj-border)] px-4 py-4 pb-safe">
              <SJChatInput
                onSend={sendMessage}
                disabled={isStreaming}
                placeholder="Describe your business challenge..."
              />
              <p className="mt-2 text-center text-[11px] text-[var(--sj-text-muted)]">
                Sanjoy provides guidance based on verified enterprise knowledge.
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
